import React from 'react';
import { Clock, CheckCircle, AlertCircle, XCircle, Loader2 } from 'lucide-react';

/**
 * Unified processing status utilities for KMS documents
 *
 * Status values (from backend KMSDocumentStatus enum):
 * - pending: Created but not uploaded to FileSearch yet
 * - processing: FileSearch upload in progress
 * - ready: Available for queries
 * - failed: Upload/processing failed
 */

export type ProcessingStatus = 'pending' | 'processing' | 'ready' | 'failed';

/**
 * Get CSS classes for status badge styling (NO HOVER)
 */
export const getProcessingStatusColor = (status: string | undefined): string => {
  if (!status) return 'bg-gray-100 text-gray-800';
  switch (status.toLowerCase()) {
    case 'ready':
      return 'bg-green-100 text-green-800';
    case 'processing':
      return 'bg-yellow-100 text-yellow-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'pending':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * @deprecated Use getProcessingStatusColor instead (no hover effects)
 * This function is kept for backward compatibility but should not be used
 */
export const getProcessingStatusBadgeClasses = (status: string): string => {
  // Just return base color without hover
  return getProcessingStatusColor(status);
};

/**
 * Get appropriate icon for processing status
 */
export const getProcessingStatusIcon = (status: string | undefined): React.ReactNode => {
  if (!status) return <AlertCircle className="h-4 w-4" />;
  switch (status.toLowerCase()) {
    case 'ready':
      return <CheckCircle className="h-4 w-4" />;
    case 'processing':
      return <Loader2 className="h-4 w-4 animate-spin" />;
    case 'failed':
      return <XCircle className="h-4 w-4" />;
    case 'pending':
      return <Clock className="h-4 w-4" />;
    default:
      return <AlertCircle className="h-4 w-4" />;
  }
};

/**
 * Get human-readable display text for status
 */
export const getProcessingStatusText = (status: string | undefined): string => {
  if (!status) return 'Unknown';
  switch (status.toLowerCase()) {
    case 'ready':
      return 'Ready';
    case 'processing':
      return 'Processing';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};
